import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  Typography,
  Divider,
  CircularProgress,
  Button,
  Stack,
  Alert,
  Skeleton,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  getCheckoutOrder,
  CheckoutOrder,
  getErrorMessage,
} from "@/api/rest/checkoutSimplifiedApi";
import FlowContainer from "@/components/pages/AppPages/Pages/components/FlowContainer";
import { useApolloClient } from "@apollo/client";
import { useAppSelector } from "@/redux/hooks";
import { fetchProductMetadataWithGraphQL } from "@/redux/reducers/checkout/thunk";
import { OrderSummaryProduct } from "./CheckoutSimplifiedForm/components/OrderSummaryProduct";
import { getHeaderLink } from "@/components/StoreComponents/StoreSinglePageOutlet";

type ProductMetadata = {
  stacklineSku: string;
  title: string;
  retailPrice: number;
  image: string;
  [key: string]: any;
};

const OrderDetails: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const userProfile = useAppSelector((state) => state.userProfile);
  const appConfig = useAppSelector((state) => state.appConfig);
  const brandEntity = useAppSelector((state) => state.brandEntity);

  const [order, setOrder] = useState<CheckoutOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [productMetadata, setProductMetadata] = useState<
    Record<string, ProductMetadata>
  >({});
  const [metadataLoading, setMetadataLoading] = useState(false);

  // Get the header link (same as brand logo)
  const headerLinkTo = getHeaderLink(
    appConfig,
    brandEntity?.entity?.name,
    brandEntity?.entity?.brandId
  );

  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderId) {
        setError("Order ID is missing");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const orderData = await getCheckoutOrder(orderId);
        setOrder(orderData);
        setError(null);

        // Fetch product metadata for line items
        if (orderData.line_items.length > 0) {
          setMetadataLoading(true);
          try {
            const retailerSkus = orderData.line_items.map((item) => item.id);
            const products = await fetchProductMetadataWithGraphQL({
              apolloClient,
              stacklineSkus: [],
              retailerSkus,
              retailerIds: [orderData.retailer.id],
              isAuthenticated: !!userProfile?.userId,
            });

            // Store product metadata
            const metadata: Record<string, ProductMetadata> = {};
            products.forEach((product: any) => {
              metadata[product.retailerSku] = {
                retailerSku: product.retailerSku,
                title: product.title || "Product",
                retailPrice: (product.retailPrice || 0) / 100,
                image: product.imageUrls?.[0] || "",
                ...product,
              };
            });
            setProductMetadata(metadata);
          } catch (metadataErr) {
            console.error("Error fetching product metadata:", metadataErr);
            // Don't fail the whole page if metadata fails
          } finally {
            setMetadataLoading(false);
          }
        }
      } catch (err) {
        console.error("Error fetching order:", err);
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId, apolloClient, userProfile?.userId]);

  const formatCurrency = (amount: number) => {
    return `$${(amount / 100).toFixed(2)}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <FlowContainer>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </FlowContainer>
    );
  }

  if (error || !order) {
    return (
      <FlowContainer>
        <Box maxWidth="600px" margin="0 auto" padding={3}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || "Order not found"}
          </Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/dashboard/purchase")}
          >
            Back to Orders
          </Button>
        </Box>
      </FlowContainer>
    );
  }

  return (
    <FlowContainer>
      <Box maxWidth="800px" margin="0 auto" padding={3}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={2} mb={3}>
          <CheckCircleIcon color="success" sx={{ fontSize: 48 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Order Confirmed
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Order #{order.id}
            </Typography>
          </Box>
        </Stack>

        {/* Order Status */}
        <Card sx={{ mb: 3, p: 3 }}>
          <Typography variant="h6" fontWeight="bold" mb={2}>
            Order Information
          </Typography>
          <Stack spacing={1.5}>
            <Box display="flex" justifyContent="space-between">
              <Typography color="text.secondary">Status</Typography>
              <Typography fontWeight="medium" textTransform="capitalize">
                {order.status.replace(/_/g, " ")}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography color="text.secondary">Order Date</Typography>
              <Typography fontWeight="medium">
                {formatDate(order.created_at)}
              </Typography>
            </Box>
          </Stack>
        </Card>

        {/* Items */}
        <Card sx={{ mb: 3, p: 3 }}>
          <Typography variant="h6" fontWeight="bold" mb={2}>
            Items
          </Typography>
          {metadataLoading ? (
            <Stack spacing={2}>
              <Skeleton variant="rectangular" height={80} />
              <Skeleton variant="rectangular" height={80} />
            </Stack>
          ) : (
            <Stack spacing={2} divider={<Divider />}>
              {order.line_items.map((item) => {
                const metadata = productMetadata[item.id];
                const product = {
                  retailerSku: item.id,
                  title: metadata?.title || item.name,
                  quantity: item.quantity,
                  retailPrice: (item.unit_price || 0) / 100,
                  image: metadata?.image || "",
                };
                return (
                  <OrderSummaryProduct key={item.id} product={product as any} />
                );
              })}
            </Stack>
          )}
        </Card>

        {/* Order Totals */}
        <Card sx={{ mb: 3, p: 3 }}>
          <Typography variant="h6" fontWeight="bold" mb={2}>
            Order Summary
          </Typography>
          <Stack spacing={1.5}>
            {order.totals.map((total) => (
              <Box
                key={total.type}
                display="flex"
                justifyContent="space-between"
              >
                <Typography
                  fontWeight={total.type === "total" ? "bold" : "regular"}
                  color={
                    total.type === "total" ? "text.primary" : "text.secondary"
                  }
                >
                  {total.display_text}
                </Typography>
                <Typography
                  fontWeight={total.type === "total" ? "bold" : "medium"}
                >
                  {formatCurrency(total.amount)}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Card>

        {/* Shipping Address */}
        <Card sx={{ mb: 3, p: 3 }}>
          <Typography variant="h6" fontWeight="bold" mb={2}>
            Shipping Address
          </Typography>
          <Stack spacing={0.5}>
            <Typography>{order.fulfillment_address.name}</Typography>
            <Typography>{order.fulfillment_address.line_one}</Typography>
            {order.fulfillment_address.line_two && (
              <Typography>{order.fulfillment_address.line_two}</Typography>
            )}
            <Typography>
              {order.fulfillment_address.city},{" "}
              {order.fulfillment_address.state}{" "}
              {order.fulfillment_address.postal_code}
            </Typography>
            <Typography>{order.fulfillment_address.country}</Typography>
          </Stack>
        </Card>

        {/* Billing Address */}
        <Card sx={{ mb: 3, p: 3 }}>
          <Typography variant="h6" fontWeight="bold" mb={2}>
            Billing Address
          </Typography>
          <Stack spacing={0.5}>
            <Typography>{order.billing_address.name}</Typography>
            <Typography>{order.billing_address.line_one}</Typography>
            {order.billing_address.line_two && (
              <Typography>{order.billing_address.line_two}</Typography>
            )}
            <Typography>
              {order.billing_address.city}, {order.billing_address.state}{" "}
              {order.billing_address.postal_code}
            </Typography>
            <Typography>{order.billing_address.country}</Typography>
          </Stack>
        </Card>

        {/* Contact Information */}
        <Card sx={{ mb: 3, p: 3 }}>
          <Typography variant="h6" fontWeight="bold" mb={2}>
            Contact Information
          </Typography>
          <Stack spacing={1}>
            <Box display="flex" justifyContent="space-between">
              <Typography color="text.secondary">Name</Typography>
              <Typography fontWeight="medium">
                {order.buyer.first_name} {order.buyer.last_name}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography color="text.secondary">Email</Typography>
              <Typography fontWeight="medium">{order.buyer.email}</Typography>
            </Box>
            {order.buyer.phone_number && (
              <Box display="flex" justifyContent="space-between">
                <Typography color="text.secondary">Phone</Typography>
                <Typography fontWeight="medium">
                  {order.buyer.phone_number}
                </Typography>
              </Box>
            )}
          </Stack>
        </Card>

        {/* Actions */}
        <Stack direction="row" spacing={2} justifyContent="center">
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => {
              if (headerLinkTo.external) {
                window.location.href = headerLinkTo.link;
              } else {
                navigate(headerLinkTo.link);
              }
            }}
          >
            Continue Shopping
          </Button>
          {userProfile?.userId && (
            <Button variant="contained" onClick={() => navigate("/purchase")}>
              View All Orders
            </Button>
          )}
        </Stack>
      </Box>
    </FlowContainer>
  );
};

export default OrderDetails;
